<template>
  <div>
    <!-- Event Details Popup -->
    <event-details-popup
      :visible.sync="showEventDetails"
      :event="currentEvent"
      @reschedule="rescheduleFromDetails"
      @cancel="cancelFromDetails"
      @confirm="confirmFromDetails" />

    <!-- Modal para Remarcar -->
    <reschedule-modal
      modal-id="student-reschedule-modal"
      :visible="showRescheduleModal"
      :title="$t('lesson.reschedule')"
      :message="rescheduleMessage"
      :justification-label="$t('lesson.justification_placeholder')"
      :justification-placeholder="$t('lesson.justification_placeholder')"
      :ok-title="$t('action.confirm')"
      :cancel-title="$t('action.cancel')"
      :error-message="$t('lesson.justification_required')"
      :reference-id="getReferenceId"
      @confirm="confirmReschedule"
      @cancel="showRescheduleModal = false"
      @update:visible="showRescheduleModal = $event" />

    <!-- Modal para Cancelar -->
    <cancel-modal
      modal-id="student-cancel-modal"
      :visible="showCancelModal"
      :title="$t('lesson.cancel_schedule')"
      :message="cancelMessage"
      :justification-label="$t('lesson.justification_placeholder')"
      :justification-placeholder="$t('lesson.justification_placeholder')"
      :ok-title="$t('yes')"
      :cancel-title="$t('no')"
      :error-message="$t('lesson.justification_required')"
      @confirm="confirmCancel"
      @cancel="showCancelModal = false"
      @update:visible="showCancelModal = $event" />

    <!-- Modal para Confirmar Conclusão -->
    <confirm-modal
      modal-id="student-confirm-modal"
      :visible="showConfirmModal"
      :title="$t('lesson.confirm_completion')"
      :message="confirmMessage"
      :justification-label="$t('lesson.observations_placeholder')"
      :justification-placeholder="$t('lesson.observations_placeholder')"
      :ok-title="$t('action.confirm')"
      :cancel-title="$t('action.cancel')"
      :require-justification="false"
      @confirm="confirmCompletion"
      @cancel="showConfirmModal = false"
      @update:visible="showConfirmModal = $event" />

    <Grid
      ref="grid"
      :service="service"
      :route_grid_path="`/students/events/grid`"
      order_column="start_at"
    >
      <template v-slot:gridThead>
        <vs-th key="title" sort-key="title">{{ $t('lesson.title') }}</vs-th>
        <vs-th key="start_at" sort-key="start_at">{{
          $t('schedule.start_at')
        }}</vs-th>
        <vs-th key="end_at" sort-key="end_at">{{
          $t('schedule.end_at')
        }}</vs-th>
        <vs-th key="status" sort-key="status">{{ $t('lesson.status') }}</vs-th>
      </template>
      <template v-slot:gridData="props">
        <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in props.data">
          <vs-td width="40%" key="title">
            <span>{{ tr.data.title }}</span>
          </vs-td>
          <vs-td width="20%" key="start_at">
            <div>{{ formatDateTime(tr.start_at) }}</div>
          </vs-td>
          <vs-td width="20%" key="end_at">
            <div>{{ formatDateTime(tr.end_at) }}</div>
          </vs-td>
          <vs-td width="10%" key="status">
            <vs-chip
              transparent
              :color="_.get(tr, 'status.color')"
              class="text-base">{{ _.get(tr, 'status.label') }}</vs-chip>
          </vs-td>
          <vs-td width="10%">
            <div class="flex flex-row gap-2">
              <feather-icon
                icon="EyeIcon"
                class="h-5 w-5 mb-1 mr-1 stroke-current text-primary cursor-pointer"
                v-tooltip.top="{
                  content: $t('action.consult'),
                  delay: { show: 100, hide: 100 }
                }"
                @click="viewDetails(tr)"
              />
              <feather-icon
                v-if="canConfirm(tr)"
                v-permission="'my_lessons.confirm'"
                icon="CheckCircleIcon"
                class="h-5 w-5 mb-1 mr-1 stroke-current text-success cursor-pointer"
                v-tooltip.top="{
                  content: $t('lesson.confirm_completion'),
                  delay: { show: 100, hide: 100 }
                }"
                @click="confirmDone(tr)"
              />
              <feather-icon
                v-if="canReschedule(tr)"
                v-permission="'my_lessons.reschedule'"
                icon="CalendarIcon"
                class="h-5 w-5 mb-1 mr-1 stroke-current text-primary cursor-pointer"
                v-tooltip.top="{
                  content: $t('lesson.reschedule'),
                  delay: { show: 100, hide: 100 }
                }"
                @click="reschedule(tr)"
              />
              <feather-icon
                v-if="canCancel(tr)"
                v-permission="'my_lessons.cancel'"
                icon="XCircleIcon"
                class="h-5 w-5 mb-1 mr-1 stroke-current text-danger cursor-pointer"
                v-tooltip.top="{
                  content: $t('lesson.cancel_schedule'),
                  delay: { show: 100, hide: 100 }
                }"
                @click="cancel(tr)"
              />
            </div>
          </vs-td>
        </vs-tr>
      </template>
    </Grid>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'

import CalendarService from '@/services/api/CalendarService'
import RescheduleModal from '@/components/RescheduleModal.vue'
import CancelModal from '@/components/CancelModal.vue'
import ConfirmModal from '@/components/ConfirmModal.vue'
import EventDetailsPopup from '@/components/EventDetailsPopup.vue'

export default {
  directives: {
    Ripple
  },
  components: {
    BButton,
    RescheduleModal,
    CancelModal,
    ConfirmModal,
    EventDetailsPopup
  },
  data: () => ({
    selected: [],
    service: null,
    currentLesson: null,
    currentEvent: {
      id: null,
      title: '',
      legal_entity_name: '',
      address: '',
      startDate: '',
      endDate: '',
      status: null
    },
    showEventDetails: false,
    showRescheduleModal: false,
    showCancelModal: false,
    showConfirmModal: false
  }),
  computed: {
    rescheduleMessage() {
      const title = this._.get(this.currentLesson, 'data.title', '')
      return `${this.$t('lesson.confirm_reschedule')} '${title}'?`
    },
    cancelMessage() {
      const title = this._.get(this.currentLesson, 'data.title', '')
      return `${this.$t('lesson.confirm_cancel')} '${title}'?`
    },
    confirmMessage() {
      const title = this._.get(this.currentLesson, 'data.title', '')
      return `${this.$t('lesson.confirm_done')} '${title}'?`
    },
    getReferenceId() {
      // Pega o reference_id pois scheduled_events é uma tabela polimórfica
      // Este é o ID da Lesson que contém os horários pré-agendados
      if (this.currentLesson) {
        return this._.get(this.currentLesson, 'reference_id') || null
      }
      return null
    }
  },
  methods: {
    formatDateTime(timestamp) {
      return this.$utils.format.shortDateWithTime(timestamp)
    },
    detail(data) {
      this.$router.push(`/lessons/students/${data.id}/detail`)
    },
    viewDetails(lesson) {
      this.currentEvent = {
        id: lesson.id,
        title: this._.get(lesson, 'data.title', ''),
        legal_entity_name: this._.get(lesson, 'legal_entity_name', ''),
        address: this._.get(lesson, 'address', ''),
        startDate: lesson.start_at,
        endDate: lesson.end_at,
        status: lesson.status
      }
      this.showEventDetails = true
    },
    rescheduleFromDetails(event) {
      this.currentLesson = {
        id: event.id,
        data: {
          title: event.title
        }
      }
      this.showRescheduleModal = true
    },
    cancelFromDetails(event) {
      this.currentLesson = {
        id: event.id,
        data: {
          title: event.title
        }
      }
      this.showCancelModal = true
    },
    confirmFromDetails(event) {
      this.currentLesson = {
        id: event.id,
        data: {
          title: event.title
        }
      }
      this.showConfirmModal = true
    },
    canConfirm(lesson) {
      // Só pode confirmar se o status NÃO for DO (Concluído) ou CA (Cancelado)
      if (!lesson.status || !lesson.status.key) return false
      const notAllowedStatuses = ['DO', 'CA']
      return !notAllowedStatuses.includes(lesson.status.key)
    },
    canReschedule(lesson) {
      // Só pode remarcar se o status NÃO for RE (Remarcado), CA (Cancelado) ou DO (Concluído)
      if (!lesson.status || !lesson.status.key) return false
      const notAllowedStatuses = ['RE', 'CA', 'DO']
      return !notAllowedStatuses.includes(lesson.status.key)
    },
    canCancel(lesson) {
      // Só pode cancelar se o status NÃO for CA (Cancelado) ou DO (Concluído)
      if (!lesson.status || !lesson.status.key) return false
      const notAllowedStatuses = ['CA', 'DO']
      return !notAllowedStatuses.includes(lesson.status.key)
    },
    reschedule(lesson) {
      this.currentLesson = lesson
      this.showRescheduleModal = true
    },
    confirmReschedule(data) {
      this.$vs.loading()

      // Prepara o payload para o backend
      const payload = {
        justification: data.justification
      }

      // Adiciona dados específicos baseados no tipo de remarcação
      if (data.scheduleType === 'available' && data.availableScheduleId) {
        // O RescheduleModal já envia o ID real do available_schedule
        payload.available_schedule_id = data.availableScheduleId
        payload.available_date = data.availableDate

        console.log('📤 Student - Remarcação com horário disponível:', {
          available_schedule_id: payload.available_schedule_id,
          available_date: payload.available_date
        })
      } else if (data.scheduleType === 'free') {
        // Envia os dados de horário livre
        payload.new_date = data.newDate
        payload.start_time = data.startTime
        payload.end_time = data.endTime

        console.log('📤 Student - Remarcação com horário livre:', {
          new_date: payload.new_date,
          start_time: payload.start_time,
          end_time: payload.end_time
        })
      }

      console.log('Payload enviado para remarcação:', payload)

      this.service.rescheduleEvent(this.currentLesson.id, payload).then((response) => {
        this.notifySuccess(this.$vs, this.$t('lesson.reschedule_success'))
      }).catch((error) => {
        this.notifyError(this.$vs, this.$t('lesson.reschedule_error'))
        console.error('Erro ao remarcar:', error)
      }).finally(() => {
        this.$vs.loading.close()
        this.showRescheduleModal = false
        this.refreshGrid()
      })
    },
    cancel(lesson) {
      this.currentLesson = lesson
      this.showCancelModal = true
    },
    confirmCancel(data) {
      this.$vs.loading()
      // Tela do próprio aluno: o endpoint de aluno é o correto aqui (ele é o
      // dono da reserva). O que faltava era avisar quando a recusa vinha da
      // política de cancelamento — sem `.catch()` o modal fechava calado.
      this.service.cancelEvent(this.currentLesson.id, data).then((response) => {
        this.notifySuccess(this.$vs, this.$t('lesson.cancel_success'))
      }).catch((error) => {
        this.notifyError(this.$vs, this.$t('lesson.cancel_error'))
        console.error('Erro ao cancelar:', error)
      }).finally(() => {
        this.$vs.loading.close()
        this.showCancelModal = false
        this.refreshGrid()
      })
    },
    confirmDone(lesson) {
      this.currentLesson = lesson
      this.showConfirmModal = true
    },
    confirmCompletion(data) {
      this.$vs.loading()
      this.service.confirmEvent(this.currentLesson.id, data).then((response) => {
        this.notifySuccess(this.$vs, this.$t('lesson.confirm_success'))
      }).finally(() => {
        this.$vs.loading.close()
        this.showConfirmModal = false
        this.refreshGrid()
      })
    },
    refreshGrid() {
      if (this.$refs.grid) {
        this.$refs.grid.fetchGridData()
      }
    }
  },
  beforeMount() {
    this.service = CalendarService.build(this.$vs)
  }
}
</script>
